<template>
  <v-container width="1200px">
    <EmployeePayrollView
      ref="employeePayrollView"
      :user="user"
      :companies="companiesList"
      :mySubmissions="mySubmissions"
      @submit="handlePayrollSubmission"
      @refresh="loadEmployeeData"
    />
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import EmployeePayrollView from "./components/EmployeePayrollView.vue";

export default {
  name: "PayrollEmployeePage",
  inject: ["users", "companies"],
  components: {
    EmployeePayrollView,
  },
  data() {
    return {
      mySubmissions: [],
      myPTSubmissions: [],
      ptFormStatus: {
        hasEmployeeDatabaseForm: false,
        hasSignatureAcknowledgement: false,
      },
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/get_user",
    }),
    companiesList() {
      return this.companies ? this.companies() : [];
    },
  },
  async mounted() {
    await this.loadEmployeeData();
  },
  methods: {
    async loadEmployeeData() {
      try {
        const submissionsResponse = await this.$axios.get('/api/payroll/my-submissions');
        if (submissionsResponse.data.response || submissionsResponse.data.data) {
          this.mySubmissions = submissionsResponse.data.data || [];
        }

        const ptSubmissionsResponse = await this.$axios.get('/api/payroll/pt/my-submissions');
        if (ptSubmissionsResponse.data.response || ptSubmissionsResponse.data.data) {
          this.myPTSubmissions = ptSubmissionsResponse.data.data || [];
        }

        const ptFormStatusResponse = await this.$axios.get('/api/payroll/pt/form-status');
        if (ptFormStatusResponse.data.response && ptFormStatusResponse.data.data) {
          this.ptFormStatus = ptFormStatusResponse.data.data;
        }
      } catch (error) {
        console.error('Error loading employee data:', error);
        this.$toast.error('Failed to load payroll data');
      }
    },

    async handlePayrollSubmission(payload) {
      try {
        const formData = new FormData();

        formData.append('company_id', payload.company?.id);
        formData.append('invoice_number', payload.invoiceNumber);
        formData.append('invoice_period_from', payload.invoicePeriod.from);
        formData.append('invoice_period_to', payload.invoicePeriod.to);
        formData.append('hours_worked', payload.hoursWorked.hours + (payload.hoursWorked.minutes / 60));
        formData.append('invoice_amount', payload.invoiceAmount);
        formData.append('currency', payload.currency);

        if (payload.invoiceFile) {
          formData.append('invoice_file', payload.invoiceFile);
        }
        if (payload.corFile) {
          formData.append('cor_file', payload.corFile);
        }
        if (payload.receiptFile) {
          formData.append('acknowledgement_receipt_file', payload.receiptFile);
        }

        if (payload.taxReturnFiles && payload.taxReturnFiles.length > 0) {
          payload.taxReturnFiles.forEach((file, index) => {
            formData.append(`tax_return_files[${index}]`, file);
          });
        }

        if (payload.timeDoctorHours !== null && payload.timeDoctorHours !== undefined) {
          formData.append('time_doctor_hours', payload.timeDoctorHours);
        }

        if (payload.comments) {
          formData.append('comments', payload.comments);
        }

        const response = await this.$axios.post('/api/payroll/submit', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.response) {
          this.$toast.success(response.data.message || 'Payroll submitted successfully');
          if (this.$refs.employeePayrollView) {
            this.$refs.employeePayrollView.showSubmissionSuccess();
          }
          await this.loadEmployeeData();
        } else {
          this.$toast.error(response.data.message || 'Failed to submit payroll');
          if (this.$refs.employeePayrollView) {
            this.$refs.employeePayrollView.enableSubmit();
          }
        }
      } catch (error) {
        console.error('Error submitting payroll:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to submit payroll');
        if (this.$refs.employeePayrollView) {
          this.$refs.employeePayrollView.enableSubmit();
        }
      }
    },

    async handlePTPayrollSubmission(payload) {
      try {
        const formData = new FormData();

        formData.append('company_id', payload.company?.id);
        formData.append('client_name', payload.clientName);
        formData.append('invoice_number', payload.invoiceNumber);
        formData.append('invoice_period_from', payload.invoicePeriod.from);
        formData.append('invoice_period_to', payload.invoicePeriod.to);
        formData.append('hours_worked', payload.hoursWorked.hours + (payload.hoursWorked.minutes / 60));
        formData.append('invoice_amount', payload.invoiceAmount);
        formData.append('currency', payload.currency || 'USD');

        if (payload.invoiceFile) {
          formData.append('invoice_file', payload.invoiceFile);
        }

        if (payload.capacityUtilizationFile) {
          formData.append('capacity_utilization_form_file', payload.capacityUtilizationFile);
        }
        if (payload.overtimeApprovalFile) {
          formData.append('overtime_approval_file', payload.overtimeApprovalFile);
        }
        if (payload.employeeDatabaseFormFile) {
          formData.append('employee_database_form_file', payload.employeeDatabaseFormFile);
        }
        if (payload.signatureAcknowledgementFile) {
          formData.append('signature_acknowledgement_file', payload.signatureAcknowledgementFile);
        }
        if (payload.receiptFile) {
          formData.append('acknowledgement_receipt_file', payload.receiptFile);
        }

        if (payload.comments) {
          formData.append('comments', payload.comments);
        }

        if (payload.timeDoctorHours !== null && payload.timeDoctorHours !== undefined) {
          formData.append('time_doctor_hours', payload.timeDoctorHours);
        }

        const response = await this.$axios.post('/api/payroll/pt/submit', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.response) {
          this.$toast.success(response.data.message || 'PT Payroll submitted successfully');
          if (this.$refs.employeePayrollView) {
            this.$refs.employeePayrollView.showPTSubmissionSuccess();
          }
          await this.loadEmployeeData();
        } else {
          this.$toast.error(response.data.message || 'Failed to submit PT payroll');
          if (this.$refs.employeePayrollView) {
            this.$refs.employeePayrollView.enableSubmit();
          }
        }
      } catch (error) {
        console.error('Error submitting PT payroll:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to submit PT payroll');
        if (this.$refs.employeePayrollView) {
          this.$refs.employeePayrollView.enableSubmit();
        }
      }
    },
  },
};
</script>

<style scoped>
</style>
